import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, NavLink, useLocation } from 'react-router-dom';
import { useAuthStore, UserAvatar } from '@entities/User';
import { useNotificationsStore } from '@features/Notifications';
import { SocialRewardSidebarRow } from '@features/SocialReward';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';
import {
  BellIcon,
  BookIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  CogIcon,
  GameIcon,
  HomeIcon,
  LightbulbIcon,
  Logo,
  ShieldIcon,
  SparklesIcon,
  UsersIcon,
  WordsIcon,
} from '@shared/ui';
import type { ComponentType, SVGProps } from 'react';
import { navSectionOf, type NavSection } from './navSections';
import { useSidebarStore } from './sidebarStore';
import { TokenBalance } from './TokenBalance';

type IconCmp = ComponentType<SVGProps<SVGSVGElement>>;

interface NavRow {
  to: string;
  labelKey: string;
  Icon: IconCmp;
  /** Раздел (navSections): пункт подсвечен на всех его страницах — модуль часть «Слов и модулей». Он же шаг знакомства
   *  (`data-tour`). Без раздела — свой адрес и вложенные. */
  section?: Exclude<NavSection, 'more'>;
  /** Пометка «новое» у новой вкладки. */
  isNew?: boolean;
}

/** Уведомления — колокольчиком у логотипа, а не вкладкой (решение владельца 2026-10-21); число непрочитанных — на нём. */
function BellLink({ count, collapsed, className }: { count: number; collapsed: boolean; className?: string }) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={paths.notifications}
      aria-label={count > 0 ? t('nav.aria.notificationsCount', { count }) : t('nav.notifications')}
      title={t('nav.notifications')}
      className={({ isActive }) =>
        cn(
          'relative grid h-9 w-9 flex-none place-items-center rounded-xl transition-colors',
          isActive ? 'bg-elev text-fg' : 'text-muted hover:bg-[#17171a] hover:text-fg',
          collapsed ? 'mx-auto' : 'ml-auto',
          className,
        )
      }
    >
      <BellIcon className="h-[18px] w-[18px]" />
      {count > 0 && (
        <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-[16px] place-items-center rounded-pill bg-accent px-1 text-[10px] font-bold leading-none text-fg-inv ring-2 ring-[#0e0e10]">
          {count > 9 ? '9+' : count}
        </span>
      )}
    </NavLink>
  );
}

interface ProfileMenuProps {
  initials: string;
  name: string;
  email: string;
  collapsed: boolean;
  items: NavRow[];
}

/**
 * Карточка профиля внизу панели открывает меню второстепенного: настройки, тарифы, админка (решение владельца
 * 2026-10-21; «Установить на телефон» — в «Ещё» телефона). Меню — порталом с fixed: у панели своя прокрутка, она
 * обрезала бы его, а свёрнутая панель (72 px) — тем более.
 */
function ProfileMenu({ initials, name, email, collapsed, items }: ProfileMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPlace(
        collapsed
          ? { left: rect.right + 8, bottom: window.innerHeight - rect.bottom, width: 224 }
          : { left: rect.left, bottom: window.innerHeight - rect.top + 8, width: rect.width },
      );
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [open, collapsed]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const itemClass = 'flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-[14px] font-medium transition-colors';
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('nav.aria.profileMenu')}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex items-center rounded-[14px] text-left transition-colors hover:bg-elev',
          open ? 'bg-elev' : 'bg-card',
          collapsed ? 'mx-auto h-12 w-12 justify-center p-0' : 'w-full gap-2.5 p-3',
        )}
      >
        {/* amp-mask / ym-hide-content: имя, email и инициалы не попадают в записи сессий. */}
        <UserAvatar initials={initials} className="amp-mask ym-hide-content" />
        {!collapsed && (
          <>
            <span className="amp-mask ym-hide-content min-w-0 flex-1">
              <span className="block truncate text-[14px] font-medium">{name}</span>
              {email && <span className="block truncate text-[12px] text-muted">{email}</span>}
            </span>
            <ChevronDownIcon
              className={cn('h-4 w-4 flex-none text-muted transition-transform', !open && 'rotate-180')}
              aria-hidden
            />
          </>
        )}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={t('nav.aria.profileMenu')}
            style={place}
            className="fixed z-[230] rounded-[14px] border border-solid border-line bg-card p-1.5 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.7)]"
          >
            {items.map(({ to, labelKey, Icon }) => (
              <NavLink
                key={to}
                to={to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(itemClass, isActive ? 'bg-elev text-fg' : 'text-muted hover:bg-[#17171a] hover:text-fg')
                }
              >
                <Icon className="h-[18px] w-[18px] shrink-0" />
                {t(labelKey)}
              </NavLink>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Боковая панель компьютера (шире 900 px). Телефон — своя навигация: панель вкладок внизу и лист «Ещё» (MobileNav,
 * решение владельца 2026-10-23).
 */
export function Sidebar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const c = useSidebarStore((s) => s.collapsed);
  const setCollapsed = useSidebarStore((s) => s.setCollapsed);
  const current = navSectionOf(pathname);

  // На виду — только основное (решение владельца 2026-10-21: «много вкладок отпугивает»; 2026-10-23 — разделы как у
  // вкладок телефона). «Слова и модули» — свои и готовые модули со строкой создания (бывшие «Модули» и «Публичные
  // модули»), уведомления — колокольчиком у логотипа, тарифы — по жетонам и в меню профиля, настройки и админка — в
  // меню профиля.
  const items: NavRow[] = [
    { to: paths.dashboard, labelKey: 'nav.dashboard', Icon: HomeIcon, section: 'home' },
    { to: paths.words, labelKey: 'nav.words', Icon: WordsIcon, section: 'words' },
    // «Грамматика» — основной раздел (владелец 2026-10-23: «не надо долго искать»).
    { to: paths.grammar, labelKey: 'nav.grammar', Icon: BookIcon, section: 'grammar' },
    // Игра «Выживи в Лондоне»: истории, миссия дня, босс из ошибок (решение владельца 2026-10-22: «назови кликбейтно»).
    { to: paths.stories, labelKey: 'nav.stories', Icon: GameIcon, section: 'game', isNew: true },
    // «Полезное»: неправильные глаголы и звуки английского с тренажёрами — как на лендинге.
    { to: paths.useful, labelKey: 'nav.useful', Icon: LightbulbIcon },
    { to: paths.community, labelKey: 'nav.community', Icon: UsersIcon },
  ];
  // Меню профиля. Админ-пункт видят только аккаунты с ролью ADMIN (выставляется вручную в БД); доступ к данным
  // всё равно проверяется на бэке — это лишь видимость.
  const profileItems: NavRow[] = [
    { to: paths.profile, labelKey: 'nav.profile', Icon: CogIcon },
    { to: paths.plans, labelKey: 'nav.plans', Icon: SparklesIcon },
    ...(user?.role === 'ADMIN' ? [{ to: paths.admin, labelKey: 'nav.admin', Icon: ShieldIcon } as NavRow] : []),
  ];

  return (
    <aside
      className={cn(
        // h-full — высота ячейки оболочки (.app-shell, 100dvh), а не 100vh окна.
        'sticky top-0 z-10 flex h-full shrink-0 flex-col border-r border-line bg-[#0e0e10] py-6 transition-[width,padding] duration-300',
        c ? 'px-3' : 'px-4',
      )}
      style={{ width: c ? 72 : 240 }}
    >
      {/* Своя прокрутка содержимого: с подарком панель не влезает в окно 720–768 px. Кнопка сворачивания —
          снаружи, иначе overflow обрезал бы её выступ за край панели. Поля -m/p — место под рамку фокуса
          (2 px и отступ 2 px), её тоже обрезал бы overflow. */}
      <div className="-m-2 flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain p-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className={cn('relative flex items-center gap-2.5 pb-6 pt-1', c ? 'justify-center px-0' : 'px-3')}>
          <Logo size={c ? 'sm' : 'md'} showName={!c} />
          {/* Колокольчик заходит в поле ряда: иначе логотипу (142 px) не хватало 5–6 px, и «LEARN ENGLISH» резалось. */}
          {!c && <BellLink count={unreadCount} collapsed={false} className="-mr-2" />}
        </div>
        {c && (
          <div className="-mt-3 pb-3">
            <BellLink count={unreadCount} collapsed />
          </div>
        )}

        <div className="flex flex-col gap-1">
          {items.map(({ to, labelKey, Icon, section, isNew }) => {
            const active = section ? current === section : !!matchPath({ path: to, end: false }, pathname);
            return (
              <Link
                key={to}
                to={to}
                data-tour={section}
                aria-current={active ? 'page' : undefined}
                title={c ? t(labelKey) : undefined}
                className={cn(
                  'group relative flex items-center rounded-xl text-left text-[14px] font-medium transition-colors',
                  active ? 'bg-elev text-fg' : 'text-muted hover:bg-[#17171a] hover:text-fg',
                  c ? 'mx-auto h-10 w-10 justify-center' : 'w-full gap-3 px-3 py-2.5',
                )}
              >
                <span className="relative grid place-items-center">
                  <Icon className="h-[18px] w-[18px] shrink-0" />
                  {/* «Новое» — точкой на значке: с плашкой «Выживи в Лондоне» не помещалось (владелец 2026-10-22:
                      «не пропечаталось название»). */}
                  {!c && isNew && (
                    <span
                      role="img"
                      aria-label={t('nav.newBadge')}
                      className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-accent ring-2 ring-[#0e0e10]"
                    />
                  )}
                </span>
                {!c && <span className="flex-1 truncate">{t(labelKey)}</span>}
                {c && isNew && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-accent ring-2 ring-[#0e0e10]" aria-hidden />}
              </Link>
            );
          })}
        </div>

        <div className="mt-auto pt-4">
          {/* Подарок за подписку — над жетонами (ТЗ главной § 11). */}
          <SocialRewardSidebarRow collapsed={c} />
          <TokenBalance variant={c ? 'collapsed' : 'panel'} />
          <ProfileMenu
            initials={user?.initials ?? '·'}
            name={user?.name || user?.email || '—'}
            // Без имени (регистрация из демо) почта уже в первой строке — второй раз не показываем.
            email={user?.name ? (user.email ?? '') : ''}
            collapsed={c}
            items={profileItems}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={() => setCollapsed(!c)}
        aria-label={c ? t('nav.aria.expand') : t('nav.aria.collapse')}
        className={cn(
          'absolute right-[-12px] top-7 z-20 grid h-6 w-6 place-items-center rounded-full border border-line bg-card text-muted shadow-[0_2px_8px_rgba(0,0,0,0.4)] transition-all duration-200 hover:border-accent/60 hover:bg-elev hover:text-fg',
          c && 'rotate-180',
        )}
      >
        <ChevronLeftIcon className="h-3 w-3" />
      </button>
    </aside>
  );
}
